import React, { useState, useRef, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { UserAuthContext } from "../../../UserAuthContext";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import "./CartDropdown.css";
import Cart from '../../../assets/images/cart.png';
import EmptyCart from '../../../assets/images/EmptyCart.png';
import Trash from '../../../assets/images/Trash.png';
import { getGuestCart, removeFromGuestCart } from '../../../utils/guestCart';
import { useLanguage } from '../../../LanguageContext';

export default function CartDropdown() {
  const [open, setOpen] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [cartUpdate, setCartUpdate] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const { user } = useContext(UserAuthContext);
  const { t } = useLanguage();

  // Utility to construct image URL
  const getImageUrl = (filename) => {
    if (!filename) {
      console.warn('No filename provided, using placeholder');
      return 'https://valleyapi-dr9y.onrender.com/data/placeholder.jpg';
    }
    const cleanFilename = filename.replace('https://valleyapi-dr9y.onrender.com/data/', '');
    const url = `https://valleyapi-dr9y.onrender.com/data/${cleanFilename}`;
    return url;
  };

  // Fetch cart items from the database
  useEffect(() => {
    const fetchCartItems = async () => {
      if (!user) {
        setCartItems(getGuestCart().map(item => ({
          id: item.idproducts,
          name: item.product_name,
          description: item.product_description,
          image: item.product_image,
          quantity: item.quantity || 1,
          price: item.product_price || 0,
        })));
        return;
      }

      const token = localStorage.getItem('user_token');
      if (!token) {
        setCartItems([]);
        return;
      }

      setIsLoading(true);
      try {
        const response = await fetch('https://valleyapi-dr9y.onrender.com/api/cart', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          console.error('Failed to fetch cart items:', errorData);
          throw new Error(errorData.message || 'Failed to fetch cart items');
        }

        const data = await response.json();
        if (!Array.isArray(data)) {
          console.error('API response is not an array:', data);
          throw new Error('Invalid API response format');
        }

        const mappedItems = data.map(item => ({
          id: item.idproducts,
          name: item.product_name,
          description: item.product_description,
          image: item.product_image,
          quantity: item.quantity || 1,
          price: item.product_price || 0,
        }));
        setCartItems(mappedItems);
      } catch (error) {
        console.error('Error fetching cart items:', error.message);
        toast.error(`Error fetching cart items: ${error.message}`, {
          toastId: 'fetch-error',
          position: "top-right",
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: true,
        });
        setCartItems([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCartItems();
  }, [user, cartUpdate]);

  // Listen for cart update events
  useEffect(() => {
    const handleCartUpdate = () => {
      setCartUpdate(prev => prev + 1);
    };

    const debouncedHandleCartUpdate = debounce(handleCartUpdate, 300);
    window.addEventListener('cartUpdated', debouncedHandleCartUpdate);
    return () => {
      window.removeEventListener('cartUpdated', debouncedHandleCartUpdate);
    };
  }, []);

  // Debounce function
  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  };

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle delete item
  const handleDeleteItem = async (idproducts, productName) => {
    if (isLoading) return;

    if (!user) {
      removeFromGuestCart(idproducts);
      setCartItems(items => items.filter(item => item.id !== idproducts));
      toast.success(t(`Removed ${productName} from cart!`, `¡${productName} se eliminó del carrito!`), {
        toastId: `no-user-${idproducts}`,
        position: "top-right",
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: true,
      });
      return;
    }

    const token = localStorage.getItem('user_token');
    if (!token) {
      toast.error(t('Session expired. Please log in again.', 'La sesión expiró. Inicie sesión nuevamente.'), {
        toastId: `no-token-${idproducts}`,
        position: "top-right",
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: true,
      });
      toast.dismiss();
      // Send the user home and auto-open the header's login panel instead of
      // the registration form (there is no dedicated /login route).
      try { sessionStorage.setItem('valley_open_auth', '1'); } catch { /* storage unavailable */ }
      navigate('/');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`https://valleyapi-dr9y.onrender.com/api/cart/${idproducts}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Failed to delete cart item:', errorData);
        throw new Error(errorData.message || 'Failed to delete cart item');
      }

      await response.json();
      toast.success(t(`Removed ${productName} from cart!`, `¡${productName} se eliminó del carrito!`), {
        toastId: `delete-${idproducts}`,
        position: "top-right",
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: true,
      });

      setCartUpdate(prev => prev + 1);
    } catch (error) {
      console.error('Error deleting cart item:', error.message);
      toast.error(t('Failed to delete item from cart. Please try again.', 'No se pudo eliminar el producto del carrito. Inténtelo de nuevo.'), {
        toastId: `delete-error-${idproducts}`,
        position: "top-right",
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoToCart = () => {
    setOpen(false);
    navigate("/cart");
  };

  const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <div className="cart-dropdown-container" ref={dropdownRef}>
      <button
        className="cart-button"
        onClick={() => setOpen(!open)}
        aria-label={t(`Cart with ${totalItems} items`, `Carrito con ${totalItems} productos`)}
        disabled={isLoading}
      >
        <img
          src={Cart}
          alt={t('Cart', 'Carrito')}
          className="cart-icon"
        />
        {totalItems > 0 && (
          <span className="cart-badge">{totalItems}</span>
        )}
      </button>

      {open && (
        <div className={`dropdown-menu ${open ? 'open' : ''}`}>
          {isLoading ? (
            <p className="loading">{t('Loading...', 'Cargando...')}</p>
          ) : cartItems.length === 0 ? (
            <div className="empty-cart-container">
              <img
                src={EmptyCart}
                alt={t('Empty cart', 'Carrito vacío')}
                className="empty-cart-icon"
              />
              <p className="empty-cart">{t('Empty cart!', '¡El carrito está vacío!')}</p>
            </div>
          ) : (
            cartItems.map((product) => (
              <div className="product-item" key={product.id}>
                <img
                  src={getImageUrl(product.image)}
                  alt={product.name}
                  className="product-image"
                  onError={(e) => {
                    e.target.src = 'https://valleyapi-dr9y.onrender.com/data/placeholder.jpg';
                  }}
                />
                <div className="product-text">
                  <span className="product-name">{product.name}</span>
                  <span className="product-description">
                    {product.description.length > 60
                      ? product.description.slice(0, 60) + "..."
                      : product.description}
                  </span>
                  <div className="product-meta">
                    {product.quantity !== undefined && (
                      <span className="product-quantity">{t('Qty', 'Cant.')}: {product.quantity}</span>
                    )}
                    {product.price !== undefined && (
                      <span className="product-price">
                        {Number(product.price) > 0 ? `$${(product.price * product.quantity).toFixed(2)}` : t('Price pending', 'Precio pendiente')}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  className="delete-button"
                  onClick={() => handleDeleteItem(product.id, product.name)}
                  aria-label={t(`Remove ${product.name} from cart`, `Eliminar ${product.name} del carrito`)}
                  disabled={isLoading}
                >
                  <img
                    src={Trash}
                    alt={t('Delete', 'Eliminar')}
                    className="delete-icon"
                  />
                </button>
              </div>
            ))
          )}
          <button
            className="go-to-cart-button"
            onClick={handleGoToCart}
            aria-label={t('Go to cart page', 'Ir a la página del carrito')}
            disabled={isLoading}
          >
            {t('Go to Cart', 'Ir al carrito')}
          </button>
        </div>
      )}
    </div>
  );
}
